import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import GettingStartedBanner from './onboarding/GettingStartedBanner';
import { menuItems, MenuItem } from './sidebar/menuItems';
import { useSidebarPins } from './sidebar/useSidebarPins';
import PinButton from './sidebar/PinButton';
import { NotificationBadge } from './ui';
import '../App.css';
import './sidebar/sidebar-icons.css';

interface SubmenuProps {
  label: string;
  icon: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const Submenu: React.FC<SubmenuProps> = ({ label, icon, isOpen, onToggle, children }) => {
  return (
    <li className="submenu-container">
      <button
        className={`submenu-toggle ${isOpen ? 'open' : ''}`}
        onClick={onToggle}
        type="button"
      >
        <span className="submenu-label">
          <i className={`fas ${icon} submenu-icon`} aria-hidden="true"></i>
          <span>{label}</span>
        </span>
        <span className="submenu-arrow">{isOpen ? '▾' : '▸'}</span>
      </button>
      <ul className={`submenu-items ${isOpen ? 'open' : ''}`}>
        {children}
      </ul>
    </li>
  );
};

const AdminLayout: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user, tenant, hasPermission, isOwner, isAdmin } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [outstandingCount, setOutstandingCount] = useState(0);
  const { pinnedItems, togglePin, isPinned } = useSidebarPins();

  useEffect(() => {
    if (!hasPermission('payments:read')) return;
    const fetchOutstanding = async () => {
      try {
        const { data } = await api.payments.getOutstandingCount();
        setOutstandingCount(data.count);
      } catch {
        // Silently fail - badge is non-critical
      }
    };

    fetchOutstanding();
    const interval = setInterval(fetchOutstanding, 5 * 60 * 1000); // Poll every 5 minutes
    return () => clearInterval(interval);
  }, [hasPermission]);

  // Check if current organization is a demo
  const currentMembership = user?.memberships?.find(
    m => m.organization.slug === tenant.slug
  );
  const isDemo = currentMembership?.organization?.subscription?.isDemo;
  const demoExpiresAt = currentMembership?.organization?.subscription?.expiresAt;

  const getDemoRemainingDays = () => {
    if (!demoExpiresAt) return 0;
    const now = new Date();
    const expires = new Date(demoExpiresAt);
    const diffTime = expires.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  // Track which submenus are open - initialize based on current route
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>(() => {
    const path = location.pathname;
    return {
      events: path.includes('/events') || path.includes('/event-templates') || path.includes('/open-events') || path.includes('/invitations'),
      activities: path.includes('/activities'),
      people: path.includes('/users') || path.includes('/members') || path.includes('/approvals') || path.includes('/groups'),
      communication: path.includes('/announcements') || path.includes('/communication'),
      finance: path.includes('/memberships') || path.includes('/payments'),
      reports: path.includes('/statistics') || path.includes('/attendance'),
      settings: path.includes('/preferences') || path.includes('/organization') || path.includes('/roles') || path.includes('/requirement-types') || path.includes('/billing'),
    };
  });

  const toggleSubmenu = (menu: string) => {
    setOpenMenus(prev => ({ ...prev, [menu]: !prev[menu] }));
  };

  const closeSidebar = () => setSidebarOpen(false);

  /** Check if a menu item is visible based on permissions */
  const isItemVisible = (item: MenuItem): boolean => {
    if (item.requireAdmin && !isOwner && !isAdmin) return false;
    if (item.permission && !hasPermission(item.permission)) return false;
    return true;
  };

  /** Get visible items for a section */
  const getSectionItems = (section: string): MenuItem[] =>
    menuItems.filter(item => item.section === section && isItemVisible(item));

  /** Render a submenu item with pin button */
  const renderSubmenuItem = (item: MenuItem) => (
    <li key={item.id} className="submenu-item-with-pin">
      <Link to={item.path} onClick={closeSidebar}>
        {t(item.labelKey)}
        {item.id === 'finance-payment-overview' && (
          <NotificationBadge count={outstandingCount} className="nav-badge-spacing" aria-label={t(item.labelKey)} />
        )}
      </Link>
      <PinButton isPinned={isPinned(item.id)} onToggle={() => togglePin(item.id)} />
    </li>
  );

  /** Get visible pinned items (filtered by current permissions, ordered by registry) */
  const visiblePinnedItems = menuItems.filter(
    item => pinnedItems.includes(item.id) && isItemVisible(item)
  );

  return (
    <div className="admin-layout">
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
        onClick={closeSidebar}
      />

      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <button
          className="sidebar-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label="Toggle Sidebar"
        >
          {sidebarOpen ? '❮' : '❯'}
        </button>
        <nav className="admin-nav">
          <ul>
            <li><Link to="/admin/calendar" onClick={closeSidebar}><i className="fas fa-calendar-days submenu-icon" aria-hidden="true"></i>{t('sidebar.calendar')}</Link></li>

            {visiblePinnedItems.length > 0 && (
              <>
                <li className="pinned-section-separator">
                  <span className="pinned-section-label">
                    <i className="fas fa-thumbtack" aria-hidden="true"></i>
                    {t('sidebar.pinned')}
                  </span>
                </li>
                {visiblePinnedItems.map(item => (
                  <li key={`pinned-${item.id}`}>
                    <Link to={item.path} onClick={closeSidebar}>
                      {t(item.labelKey)}
                      {item.id === 'finance-payment-overview' && (
                        <NotificationBadge count={outstandingCount} className="nav-badge-spacing" aria-label={t(item.labelKey)} />
                      )}
                    </Link>
                  </li>
                ))}
                <li className="pinned-section-end-separator" />
              </>
            )}

            {getSectionItems('events').length > 0 && (
              <Submenu
                label={t('sidebar.events')}
                icon="fa-calendar-check"
                isOpen={openMenus.events || false}
                onToggle={() => toggleSubmenu('events')}
              >
                {getSectionItems('events').map(renderSubmenuItem)}
              </Submenu>
            )}

            {getSectionItems('activities').length > 0 && (
              <Submenu
                label={t('sidebar.activities')}
                icon="fa-layer-group"
                isOpen={openMenus.activities || false}
                onToggle={() => toggleSubmenu('activities')}
              >
                {getSectionItems('activities').map(renderSubmenuItem)}
              </Submenu>
            )}

            {getSectionItems('people').length > 0 && (
              <Submenu
                label={t('sidebar.people')}
                icon="fa-users"
                isOpen={openMenus.people || false}
                onToggle={() => toggleSubmenu('people')}
              >
                {getSectionItems('people').map(renderSubmenuItem)}
              </Submenu>
            )}

            {getSectionItems('communication').length > 0 && (
              <Submenu
                label={t('sidebar.communication')}
                icon="fa-comments"
                isOpen={openMenus.communication || false}
                onToggle={() => toggleSubmenu('communication')}
              >
                {getSectionItems('communication').map(renderSubmenuItem)}
              </Submenu>
            )}

            {getSectionItems('finance').length > 0 && (
              <Submenu
                label={t('sidebar.finance')}
                icon="fa-wallet"
                isOpen={openMenus.finance || false}
                onToggle={() => toggleSubmenu('finance')}
              >
                {getSectionItems('finance').map(renderSubmenuItem)}
              </Submenu>
            )}

            {getSectionItems('reports').length > 0 && (
              <Submenu
                label={t('sidebar.reports')}
                icon="fa-chart-line"
                isOpen={openMenus.reports || false}
                onToggle={() => toggleSubmenu('reports')}
              >
                {getSectionItems('reports').map(renderSubmenuItem)}
              </Submenu>
            )}

            {getSectionItems('settings').length > 0 && (
              <Submenu
                label={t('sidebar.settings')}
                icon="fa-gear"
                isOpen={openMenus.settings || false}
                onToggle={() => toggleSubmenu('settings')}
              >
                {getSectionItems('settings').map(renderSubmenuItem)}
              </Submenu>
            )}
          </ul>
        </nav>
      </aside>
      <main className="admin-main-content">
        {isDemo && (
          <div className="demo-banner">
            <i className="fas fa-flask"></i>
            <span>
              {t('common.demoBannerText', { days: getDemoRemainingDays() })}
            </span>
            <Link to="/admin/billing" className="demo-banner-link">
              {t('common.upgradeNow')}
            </Link>
          </div>
        )}
        <GettingStartedBanner />
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
